/**
 * Issue: #5997
 *
 * SearchResults — ハイブリッド検索ヒットの一覧表示.
 * <mark>...</mark> highlight を安全にレンダリングし、クリックで該当
 * メッセージへ jump (onJump コールバック).
 */
import { parseExcerpt } from "./highlight";
import type { SearchHit, SearchResult } from "./types";

export interface SearchResultsProps {
  result: SearchResult | null;
  roomNames?: Record<string, string>;
  loading?: boolean;
  error?: string | null;
  onJump?: (hit: SearchHit) => void;
}

export function SearchResults(props: SearchResultsProps): JSX.Element {
  const { result, loading, error, onJump, roomNames = {} } = props;
  if (loading) {
    return (
      <div className="search-results search-results--loading" role="status" aria-busy="true">
        <span className="sr-only">検索中…</span>
        <div className="search-results__skeleton" aria-hidden="true">検索中…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="search-results search-results--error" role="alert">
        <p>検索に失敗しました: {error}</p>
      </div>
    );
  }
  if (!result) {
    return (
      <div className="search-results search-results--empty">
        <p>キーワードを入力して検索してください。</p>
      </div>
    );
  }
  if (result.hits.length === 0) {
    return (
      <div className="search-results search-results--zero">
        <p>該当するメッセージはありませんでした。</p>
      </div>
    );
  }
  return (
    <div className="search-results">
      <header className="search-results__header" aria-label="検索結果サマリー">
        <span data-testid="search-result-count">{result.total}件</span>
        <span className="search-results__took">({result.took_ms}ms)</span>
      </header>
      <ul className="search-results__list" data-testid="search-result-list">
        {result.hits.map((hit) => (
          <li key={hit.message_id} className="search-results__item">
            <button
              type="button"
              className="search-results__hit"
              data-testid="search-result-item"
              data-message-id={hit.message_id}
              onClick={() => onJump?.(hit)}
            >
              <div className="search-results__hit-header">
                <span className="search-results__room">{roomNames[hit.room_id] || hit.room_id}</span>
                {hit.sender?.kind === "integration" ? (
                  <span className="search-results__author">{hit.sender.display_name} · アプリ</span>
                ) : null}
                <time className="search-results__time" dateTime={new Date(hit.created_at).toISOString()}>
                  {new Date(hit.created_at).toLocaleString("ja-JP")}
                </time>
              </div>
              <p className="search-results__excerpt">{renderExcerpt(hit.excerpt)}</p>

            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function renderExcerpt(excerpt: string): JSX.Element[] {
  const tokens = parseExcerpt(excerpt);
  if (tokens.length === 0) {
    return [<span key="empty">(本文なし)</span>];
  }
  return tokens.map((tok, i) =>
    tok.type === "mark" ? (
      <mark key={i} className="search-results__mark">
        {tok.text}
      </mark>
    ) : (
      <span key={i}>{tok.text}</span>
    ),
  );
}
